// Popup de reclassification : choix d'un compte cible avec autocomplete.
//
// Utilisé pour :
// - Une seule écriture (clic sur bouton "···" d'une EntryRow)
// - Plusieurs écritures sélectionnées (bouton "Reclasser" de la BulkToolbar)
//
// Sur confirmation : POST /api/entries/:id/reclassify (ou bulk-reclassify si plusieurs)
// + invalidation react-query pour rafraîchir P&L, Bilan, drilldown des comptes.

import { useEffect, useRef, useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { apiPost } from "../lib/api";
import { useAccountSearch } from "../lib/use-account-search";
import { Overlay } from "./overlay";

export function ReclassifyMenu({
  entryIds,
  currentAccountCode,
  onClose,
}: {
  entryIds: number[];
  currentAccountCode?: string;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { accounts, isPending, debouncedQuery } = useAccountSearch(query);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Reset highlight quand la liste change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [debouncedQuery]);

  const mutation = useMutation({
    mutationFn: async (newAccountCode: string) => {
      if (entryIds.length === 1) {
        return apiPost(`/api/entries/${entryIds[0]}/reclassify`, { newAccountCode });
      }
      return apiPost(`/api/entries/bulk-reclassify`, { entryIds, newAccountCode });
    },
    onSuccess: () => {
      // Invalide tous les rapports + drilldowns pour rafraîchir
      queryClient.invalidateQueries({ queryKey: ["pnl"] });
      queryClient.invalidateQueries({ queryKey: ["balance-sheet"] });
      queryClient.invalidateQueries({ queryKey: ["financial-summary"] });
      queryClient.invalidateQueries({ queryKey: ["pieces"] });
      queryClient.invalidateQueries({ queryKey: ["entries"] });
      onClose();
    },
  });

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, accounts.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && accounts[highlightedIndex]) {
      e.preventDefault();
      mutation.mutate(accounts[highlightedIndex].code);
    }
  }

  return (
    <Overlay onClose={onClose}>
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="text-sm text-gray-500 mb-2">
            Reclasser {entryIds.length} écriture{entryIds.length > 1 ? "s" : ""} dans...
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Code (60415) ou libellé (Aerial, salaires...)"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isPending && <div className="px-4 py-3 text-sm text-gray-400">Chargement...</div>}
          {!isPending && accounts.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-400">Aucun compte trouvé</div>
          )}
          {accounts.map((a, idx) => {
            const isCurrent = a.code === currentAccountCode;
            const isHighlighted = idx === highlightedIndex;
            return (
              <button
                key={a.code}
                onClick={() => mutation.mutate(a.code)}
                disabled={isCurrent || mutation.isPending}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 ${
                  isCurrent
                    ? "bg-blue-50 cursor-not-allowed opacity-60"
                    : isHighlighted
                      ? "bg-gray-100"
                      : "hover:bg-gray-50"
                }`}
              >
                <span className="font-mono text-xs text-gray-500 w-20 shrink-0">
                  {a.code}
                </span>
                <span className="text-gray-800 grow">{a.label}</span>
                {isCurrent && (
                  <span className="text-xs text-blue-600 shrink-0">(actuel)</span>
                )}
              </button>
            );
          })}
        </div>

        {mutation.isError && (
          <div className="px-4 py-2 bg-red-50 text-red-700 text-xs border-t border-red-200">
            Erreur : {(mutation.error as Error).message}
          </div>
        )}

        <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-400 flex justify-between">
          <span>↑↓ pour naviguer, ↵ pour valider, Esc pour fermer</span>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            Annuler
          </button>
        </div>
    </Overlay>
  );
}
